import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Users, 
  Plus, 
  Search,
  Loader2,
  AlertCircle
} from 'lucide-react';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { useQuickActions } from '../contexts/QuickActionsContext';
import PatientDetailModal from '@/components/modals/patient/PatientDetailModal';
import EditPatientModal from '@/components/modals/patient/EditPatientModal';
import CreatePatientModal from '@/components/modals/patient/CreatePatientModal';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import { toast } from '@/components/ui/sonner';
import PatientCard from '@/components/PatientCard';
import PaginationControls from '@/components/PaginationControls';
import type { AppDispatch } from '../store/store';
import type { CreatePatientRequest } from '../types/api';
import { 
  fetchPatients,
  fetchPatientsPage,
  searchPatients,
  createPatient,
  updatePatient,
  deletePatient,
  setSearchTerm,
  clearError,
  optimisticAddPatient,
  optimisticUpdatePatient,
  optimisticDeletePatient,
  selectFilteredPatients,
  selectPatientsLoading,
  selectPatientsError,
  selectSearchTerm,
  selectIsSearching,
  selectPatientsTotal,
  selectPatientsPagination,
  selectPageNext,
  selectPagePrevious
} from '../store/slices/patientsSlice';
import { selectCurrentClinic, selectClinicsLoading } from '../store/slices/authSlice';
import type { Api } from '../types/generated';

// Temporary patient card component (until we update the existing ones)


const PatientsRedux = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openCreatePatientModal } = useQuickActions();
  
  // Redux selectors
  const patients = useSelector(selectFilteredPatients);
  const loading = useSelector(selectPatientsLoading);
  const isSearching = useSelector(selectIsSearching);
  const error = useSelector(selectPatientsError);
  const searchTerm = useSelector(selectSearchTerm);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  const total = useSelector(selectPatientsTotal);
  const paginationData = useSelector(selectPatientsPagination);
  const pageNext = useSelector(selectPageNext);
  const pagePrevious = useSelector(selectPagePrevious);

  // Local UI state using new Patient type
  const [selectedPatient, setSelectedPatient] = useState<Api.Patient | null>(null);
  const [editingPatient, setEditingPatient] = useState<Api.Patient | null>(null);
  const [patientToDelete, setPatientToDelete] = useState<Api.Patient & { id: string; href: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          dispatch(searchPatients(searchTerm));
        }, 300); // 300ms debounce
      };
    })(),
    [dispatch]
  );

  // Fetch patients on component mount and when clinic changes
  useEffect(() => {
    // GATE: Wait for clinic loading to complete
    if (clinicsLoading) {
      console.log('[Patients] Waiting for clinics to load...');
      return;
    }

    // GATE: Ensure we have clinic data with patients href
    if (!currentClinic?.patients?.href) {
      console.log('[Patients] No patients href available yet');
      return;
    }

    // NOW SAFE: Make API call
    console.log('[Patients] Loading patients from:', currentClinic.patients.href);
    dispatch(fetchPatients({}));
  }, [dispatch, currentClinic, clinicsLoading]);

  // Handle search term changes with server-side search
  const handleSearchChange = (value: string) => {
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    if (!loading) {
      dispatch(fetchPatientsPage({ page }));
    }
  };

  const handleNextPage = () => {
    if (pageNext && !loading) {
      dispatch(fetchPatientsPage({ hateoasUrl: pageNext }));
    }
  };

  const handlePreviousPage = () => {
    if (pagePrevious && !loading) {
      dispatch(fetchPatientsPage({ hateoasUrl: pagePrevious }));
    }
  };


  // HATEOAS-style: Use patient's href for updates
  const handleUpdatePatient = async (patientId: string, patientData: Partial<Api.Patient>) => {
    try {
      // Find the patient to get its href
      const patient = patients.find(p => p.id === patientId) as Api.Patient & { id: string; href: string };
      if (!patient) {
        throw new Error('Patient not found');
      }
      
      // In HATEOAS, each patient has its own href for operations
      await dispatch(updatePatient({ 
        patientHref: patient.href, 
        patientData 
      })).unwrap();
      setEditingPatient(null);
      
      // Show success toast
      const patientName = `${patientData.first_name || patient.first_name} ${patientData.last_name || patient.last_name}`;
      toast.success(`${patientName} updated successfully`);
    } catch (error) {
      console.error('Failed to update patient:', error);
      toast.error('Failed to update patient. Please try again.');
    }
  };

  // HATEOAS-style: Use patient's href for deletion with optimistic update
  const handleDeletePatient = (patient: Api.Patient & { id: string; href: string }) => {
    setPatientToDelete(patient);
  };

  const confirmDeletePatient = async () => {
    if (!patientToDelete) return;
    
    const patientName = `${patientToDelete.first_name} ${patientToDelete.last_name}`;
    setIsDeleting(true);
    
    // Optimistic update - remove immediately from UI
    dispatch(optimisticDeletePatient(patientToDelete.id));
    
    try {
      // API call in background
      await dispatch(deletePatient({ 
        patientId: patientToDelete.id,
        patientHref: patientToDelete.href 
      })).unwrap();
      
      // Show success toast
      toast.success(`${patientName} deleted successfully`);
    } catch (error) {
      console.error('Failed to delete patient:', error);
      // On failure, refresh the list to restore correct state
      dispatch(fetchPatients({}));
      // Show error toast
      toast.error(`Failed to delete ${patientName}. Please try again.`);
    } finally {
      setIsDeleting(false);
      setPatientToDelete(null);
    }
  };

  const cancelDeletePatient = () => {
    setPatientToDelete(null);
    setIsDeleting(false);
  };

  // Handle patient click
  const handlePatientClick = (patient: Api.Patient) => {
    setSelectedPatient(patient);
  };

  // Check for URL parameters to open modals
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      // Remove the parameter from URL to clean up
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:pl-24 lg:pr-8 py-8">
        {/* Header Section */}
        <div className="flex flex-col space-y-4 mb-8 md:flex-row md:justify-between md:items-center md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-syne font-medium text-vitl-navy mb-2">
              Patient Management 
            </h1>
            <p className="text-vitl-dark/70 font-lato text-sm md:text-base">
              Manage your patient database and track their medical history.
            </p>
          </div>
          <Button 
            onClick={openCreatePatientModal}
            className="bg-vitl-green hover:bg-vitl-green/90 text-white font-lato font-medium self-start md:self-auto"
            disabled={loading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Patient
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => dispatch(clearError())}
              className="ml-auto"
            >
              Dismiss
            </Button>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-vitl-green rounded-full"></div>
              </div>
            )}
            <Input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
              className={`pl-10 font-lato ${isSearching ? 'pr-10' : ''}`}
            />
          </div>
          {searchTerm && !isSearching && (
            <p className="text-sm text-gray-500 mt-2">
              Found {total} patient{total !== 1 ? 's' : ''}
            </p>
          )}
          {isSearching && (
            <p className="text-sm text-gray-500 mt-2">
              Searching...
            </p>
          )}
        </div>

        {/* Loading State - show during clinic loading OR patient loading */}
        {(loading || clinicsLoading) && !searchTerm && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
            <span className="ml-2 text-gray-600">
              {clinicsLoading ? 'Loading clinic data...' : 'Loading patients...'}
            </span>
          </div>
        )}

        {/* Patients Grid */}
        {((!loading && !clinicsLoading) || searchTerm) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {patients.map((patient) => (
                <PatientCard
                  key={patient.id}
                  patient={patient}
                  onEdit={() => setEditingPatient(patient)}
                  onDelete={() => handleDeletePatient(patient)}
                  onClick={() => handlePatientClick(patient)}
                />
              ))}
            </div>

            {/* Empty State */}
            {patients.length === 0 && !loading && !clinicsLoading && !isSearching && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-syne font-medium text-vitl-navy mb-2">
                  No patients found
                </h3>
                <p className="text-vitl-dark/70 font-lato">
                  {searchTerm ? 'Try adjusting your search terms.' : 'Add your first patient to get started.'}
                </p>
              </div>
            )}

            {/* Pagination Controls */}
            {patients.length > 0 && (
              <PaginationControls
                paginationData={paginationData}
                onPageChange={handlePageChange}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
                loading={loading || isSearching}
                className="mt-8"
              />
            )}
          </>
        )}

        

        {/* Patient Detail Modal */}
        <PatientDetailModal
          patient={selectedPatient}
          isOpen={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
          onEdit={() => {
            if (selectedPatient) {
              setEditingPatient(selectedPatient);
            }
            setSelectedPatient(null);
          }}
          onCreateOrder={() => {
            // TODO: Implement create order
            console.log('Create order for patient:', selectedPatient);
            setSelectedPatient(null);
          }}
        />

        {/* Edit Patient Modal */}
        <EditPatientModal
          patient={editingPatient}
          isOpen={!!editingPatient}
          onClose={() => setEditingPatient(null)}
          onSave={handleUpdatePatient}
          loading={loading}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={!!patientToDelete}
          onClose={cancelDeletePatient}
          onConfirm={confirmDeletePatient}
          title="Delete Patient"
          entityName={patientToDelete ? `${patientToDelete.first_name} ${patientToDelete.last_name}` : ''}
          entityDetails={patientToDelete ? `${patientToDelete.email} • ${patientToDelete.phone}` : ''}
          message="This action cannot be undone and will permanently remove all patient data, including medical history, appointments, and records."
          confirmText="Delete Patient"
          cancelText="Cancel"
          variant="danger"
          loading={isDeleting}
        />

      </main>
    </div>
  );
};

export default PatientsRedux;